import React, { useMemo } from "react";
import MentionText from "./MentionText";

// ── Slack-style lightweight block renderer for message text.
//    Turns runs of "- "/"* "/"• " lines into bullet lists and "1. " lines into
//    numbered lists, while every line's inline content still flows through
//    MentionText so @mentions and links keep working inside list items.
//    Non-list text is rendered exactly as before (whitespace preserved).

const BULLET_RE = /^(\s*)[-*•]\s+(.*)$/;
const NUM_RE = /^(\s*)(\d+)\.\s+(.*)$/;

export function hasMarkdownList(text = "") {
  return /^[ \t]*([-*•]\s+|\d+\.\s+)/m.test(text);
}

function parseBlocks(text) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (BULLET_RE.test(lines[i])) {
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(BULLET_RE.exec(lines[i])[2]);
        i++;
      }
      blocks.push({ type: "ul", items });
    } else if (NUM_RE.test(lines[i])) {
      const items = [];
      const start = parseInt(NUM_RE.exec(lines[i])[2], 10);
      while (i < lines.length && NUM_RE.test(lines[i])) {
        items.push(NUM_RE.exec(lines[i])[3]);
        i++;
      }
      blocks.push({ type: "ol", items, start: Number.isNaN(start) ? 1 : start });
    } else {
      const textLines = [];
      while (
        i < lines.length &&
        !BULLET_RE.test(lines[i]) &&
        !NUM_RE.test(lines[i])
      ) {
        textLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: "text", text: textLines.join("\n") });
    }
  }
  return blocks;
}

export default function RichMessageText({ text, currentUser, className = "" }) {
  const blocks = useMemo(() => parseBlocks(text || ""), [text]);

  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-0.5 my-1">
              {b.items.map((it, j) => (
                <li key={j} className="break-words">
                  <MentionText text={it} currentUser={currentUser} />
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol
              key={i}
              start={b.start}
              className="list-decimal pl-5 space-y-0.5 my-1"
            >
              {b.items.map((it, j) => (
                <li key={j} className="break-words">
                  <MentionText text={it} currentUser={currentUser} />
                </li>
              ))}
            </ol>
          );
        }
        return (
          <span key={i} className="whitespace-pre-wrap break-words">
            <MentionText text={b.text} currentUser={currentUser} />
          </span>
        );
      })}
    </div>
  );
}
